import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  PageContent,
  PageSection,
  PageHeader,
  ControlContainer,
  ControlContainerRow,
} from 'common/styles/page';
import ToggleControl from '../../../common/components/ToggleChartControl';
import AnalyticsResourcesMain from './AnalyticsResourcesMain';
import AnalyticsResourceMember from './AnalyticsResourceMember';
import { usePageCopilotPrompts } from 'areas/copilot/components/CopilotPromptsContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);
const CHART_TYPE_FILE = 'file';
const CHART_TYPE_USER = 'user';

const AnalyticsResources = () => {
  const [chartType, setChartType] = useState('file');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const { setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro } = usePageCopilotPrompts();

  useEffect(() => {
    setPagePrompts([
      {
        title: 'Resource Access Review',
        displayText: 'Analyze resource access and distribution.',
        prompt:
          'Review the Resource Access page and explain how learning resources are being accessed by file and by team member.',
      },
      {
        title: 'Access Equity',
        displayText: 'Identify whether resource access is balanced across the team.',
        prompt:
          'Identify whether resource access is balanced across teams and individuals. Note any employees or groups with disproportionately high or low access, and explain what that may mean for learning equity and development readiness.',
      },
      {
        title: 'Suggested Learning Actions',
        displayText: 'Recommend next steps to improve resource engagement.',
        prompt:
          'Based on the resource access data, recommend next steps to increase engagement and ensure learning resources reach the right people. Focus on specific follow-up actions for team leaders.',
      },
    ]);

    setPageInfoTitle('About Resource Access');
    setPageInfoIntro(
      'This page shows how learning resources are being accessed across the organization. It helps leaders identify which materials are being used, who is engaging with them, and whether access is evenly distributed.',
    );
    setPageInfoItems([
      {
        term: 'Usage distribution',
        description: 'Understand whether learning material usage is concentrated or widespread.',
      },
      {
        term: 'Access gaps',
        description: 'Spot employees or groups who may need more support or encouragement to engage.',
      },
      {
        term: 'Action recommendations',
        description: 'Turn access insights into concrete next steps for team leaders.',
      },
    ]);

    return () => {
      setPagePrompts([]);
      setPageInfoItems([]);
      setPageInfoTitle(undefined);
      setPageInfoIntro(undefined);
    };
  }, [setPagePrompts, setPageInfoItems, setPageInfoTitle, setPageInfoIntro]);

  return (
    <PageContent>
      <PageSection padright>
        <PageHeader
          title="Resource Access"
          caption="Review access to learning resources by file and team member."
          controls={
            <ControlContainerRow>
              <ControlContainer justify="left">
                <ToggleControl
                  buttonLabels={[
                    { type: CHART_TYPE_FILE, icon: 'file outline' },
                    { type: CHART_TYPE_USER, icon: 'user outline' },
                  ]}
                  setChartType={setChartType}
                  activeToggle={chartType}
                />
              </ControlContainer>
            </ControlContainerRow>
          }
        />
      </PageSection>
      {chartType === CHART_TYPE_FILE ? (
        <AnalyticsResourcesMain
          onSelectMember={(employeeId: string) => {
            setSelectedEmployeeId(employeeId);
            setChartType(CHART_TYPE_USER);
          }}
          selectedFilename={selectedFile}
        />
      ) : chartType === CHART_TYPE_USER ? (
        <AnalyticsResourceMember
          selectedEmployeeId={selectedEmployeeId}
          onSelectFile={(filename: string) => {
            setSelectedFile(filename);
            setChartType(CHART_TYPE_FILE);
          }}
        />
      ) : null}
    </PageContent>
  );
};

export default AnalyticsResources;
